export const PAGE_HEALTH_MENU_TAG = "fishy-page-health-menu";

export const PAGE_HEALTH_MENU_EVENTS = Object.freeze({
  cancelReport: "fishystuff:page-health-report-cancel",
  confirmReport: "fishystuff:page-health-report-confirm",
  copyIssue: "fishystuff:page-health-copy-issue",
  dismissAll: "fishystuff:page-health-dismiss-all",
  dismissIssue: "fishystuff:page-health-dismiss-issue",
  menuVisibilityChange: "fishystuff:page-health-menu-visibility-change",
  sendReport: "fishystuff:page-health-send-report",
});

const HTMLElementBase = globalThis.HTMLElement ?? class {};

function text(node, value) {
  if (!node) {
    return;
  }
  const next = String(value ?? "");
  if (node.textContent !== next) {
    node.textContent = next;
  }
}

function setClassName(node, value) {
  if (node && node.className !== value) {
    node.className = value;
  }
}

function setBooleanProperty(node, property, value) {
  if (node && node[property] !== value) {
    node[property] = value;
  }
}

function menuMarkup() {
  return `
    <button type="button"
            popovertarget="fishystuff-page-health-popover"
            style="anchor-name:--fishystuff-page-health-anchor"
            class="btn btn-circle btn-sm border border-warning/35 bg-warning/12 text-warning shadow-sm backdrop-blur hover:border-warning/60 hover:bg-warning/20"
            aria-haspopup="menu"
            aria-expanded="false"
            aria-label=""
            data-role="trigger"
            data-fishy-tooltip=""
            data-fishy-tooltip-mode="anchor-only"
            data-i18n-attr-aria-label="health.menu.open"
            data-i18n-attr-data-fishy-tooltip="health.menu.open">
      <span class="indicator">
        <span class="badge badge-warning badge-xs indicator-item min-w-4 px-1 text-[10px]" data-health-count>0</span>
        <svg class="fishy-icon size-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-alert-triangle"></use></svg>
      </span>
    </button>
    <div class="dropdown card card-sm card-border bg-base-100 z-10 mt-3 w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden shadow-2xl"
         popover
         id="fishystuff-page-health-popover"
         style="position-anchor:--fishystuff-page-health-anchor">
      <div class="card-body gap-4 p-4">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="inline-flex items-center gap-2 text-sm font-semibold text-base-content">
              <svg class="fishy-icon size-4 shrink-0 text-warning" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-alert-triangle"></use></svg>
              <span data-i18n-text="health.menu.title">Page status</span>
            </div>
            <p class="mt-1 text-xs leading-5 text-base-content/60" data-health-summary>0 active warnings</p>
          </div>
          <span class="badge badge-warning badge-sm shrink-0" data-health-count>0</span>
        </div>
        <div class="grid max-h-[18rem] gap-2 overflow-y-auto pr-1" data-health-issue-list></div>
        <p class="rounded-box border border-warning/30 bg-warning/10 px-3 py-2 text-xs leading-5 text-base-content/70" data-health-preferences-status hidden></p>
        <fieldset class="fieldset">
          <label class="label font-semibold text-base-content" for="fishystuff-page-health-report-description">What happened?</label>
          <textarea id="fishystuff-page-health-report-description"
                    class="textarea textarea-sm min-h-24 w-full resize-y"
                    rows="4"
                    required
                    disabled
                    autocomplete="off"
                    placeholder="Describe what you were doing and what went wrong."
                    aria-describedby="fishystuff-page-health-description-detail"
                    data-health-report-description></textarea>
          <div id="fishystuff-page-health-description-detail" class="flex items-start justify-between gap-3 text-xs leading-5 text-base-content/55">
            <span>Your plain-text description is included exactly in the preview and report.</span>
            <span class="shrink-0 tabular-nums" aria-live="polite" data-health-report-description-count>0 / 0</span>
          </div>
        </fieldset>
        <div class="flex flex-wrap gap-2">
          <button type="button" class="btn btn-primary btn-sm flex-1" disabled data-health-send-report aria-describedby="fishystuff-page-health-description-detail fishystuff-page-health-send-detail">
            <svg class="fishy-icon size-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-bug-fill"></use></svg>
            <span data-i18n-text="health.menu.send_report">Send report</span>
          </button>
          <button type="button" class="btn btn-ghost btn-sm" data-health-dismiss-all data-i18n-text="health.menu.dismiss_all">Dismiss all</button>
        </div>
        <p id="fishystuff-page-health-send-detail" class="text-xs leading-5 text-base-content/55" data-health-send-detail hidden></p>
        <label class="flex cursor-pointer items-center justify-between gap-3 rounded-box border border-base-300 bg-base-200/45 px-3 py-2 text-xs">
          <span class="min-w-0">
            <span class="block font-semibold text-base-content" data-i18n-text="health.menu.show_status">Show status menu</span>
            <span class="block text-base-content/55" data-health-hidden-label>Visible when warnings are active</span>
          </span>
          <input type="checkbox" class="toggle toggle-warning toggle-sm shrink-0" checked data-health-menu-toggle>
        </label>
      </div>
    </div>
    <dialog class="modal modal-bottom sm:modal-middle" data-health-report-preview>
      <div class="modal-box w-11/12 max-w-3xl">
        <h3 class="text-lg font-bold" data-i18n-text="health.report.preview_title">Review this diagnostic report</h3>
        <div role="alert" class="alert alert-info alert-soft mt-3">
          <span data-i18n-text="health.report.preview_notice">Only the exact JSON below is sent when you choose “Send this report”. The fixed OTLP envelope adds the event timestamp, fishystuff-site service name, severity, and bug_report signal.</span>
        </div>
        <div class="mockup-code mt-4 w-full">
          <pre class="max-h-[50vh] overflow-auto whitespace-pre-wrap break-words px-5"><code data-health-report-preview-json></code></pre>
        </div>
        <div class="modal-action">
          <button type="button" class="btn btn-sm" data-health-report-cancel data-i18n-text="health.report.preview_cancel">Cancel</button>
          <button type="button" class="btn btn-primary btn-sm" data-health-report-confirm data-i18n-text="health.report.preview_confirm">Send this report</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button data-health-report-cancel aria-label="Cancel diagnostic report preview">close</button>
      </form>
    </dialog>
  `;
}

function issueRow(rootDocument, issue) {
  const row = rootDocument.createElement("div");
  row.className = "rounded-box border border-base-300 bg-base-200/45 p-3";
  row.innerHTML = `
    <div class="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3 gap-y-2 overflow-hidden">
      <label class="label shrink-0 self-start p-0" aria-label="Include this issue in the diagnostic report">
        <input type="checkbox" class="checkbox checkbox-sm" checked data-health-report-select-id="">
      </label>
      <div class="min-w-0 break-words text-sm font-semibold text-base-content" data-health-issue-title></div>
      <button type="button" class="btn btn-ghost btn-xs shrink-0" data-health-dismiss-id="" data-i18n-text="health.issue.dismiss">Dismiss</button>
      <button type="button" class="btn btn-ghost btn-xs btn-square shrink-0 self-start" data-health-copy-id="" data-fishy-tooltip="Copy details" aria-label="Copy details" data-i18n-attr-data-fishy-tooltip="health.issue.copy_details" data-i18n-attr-aria-label="health.issue.copy_details">
        <svg class="fishy-icon size-4" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-copy"></use></svg>
      </button>
      <div class="col-span-2 flex min-w-0 flex-wrap items-center gap-2">
        <span class="badge badge-sm" data-health-issue-severity></span>
        <span class="badge badge-outline badge-sm" data-health-issue-source></span>
        <span class="badge badge-ghost badge-sm" data-health-issue-count></span>
      </div>
      <p class="col-span-3 max-h-24 min-w-0 max-w-full overflow-auto whitespace-pre-wrap break-words rounded-box bg-base-100/60 px-2 py-1.5 text-xs leading-5 text-base-content/65" data-health-issue-detail></p>
    </div>
  `;
  text(row.querySelector("[data-health-issue-title]"), issue.title);
  const detail = row.querySelector("[data-health-issue-detail]");
  text(detail, issue.detail);
  detail.hidden = !issue.detail;
  const severity = row.querySelector("[data-health-issue-severity]");
  setClassName(severity, `badge badge-sm badge-${issue.severity}`);
  text(severity, issue.severityLabel);
  text(row.querySelector("[data-health-issue-source]"), issue.source);
  const count = row.querySelector("[data-health-issue-count]");
  text(count, issue.countLabel);
  count.hidden = issue.count <= 1;
  const copy = row.querySelector("[data-health-copy-id]");
  copy.hidden = issue.copyAvailable !== true;
  copy.setAttribute("data-health-copy-id", issue.id);
  row.querySelector("[data-health-report-select-id]")
    .setAttribute("data-health-report-select-id", issue.id);
  row.querySelector("[data-health-dismiss-id]")
    .setAttribute("data-health-dismiss-id", issue.id);
  return row;
}

export class FishyPageHealthMenu extends HTMLElementBase {
  static events = PAGE_HEALTH_MENU_EVENTS;

  #connected = false;
  #snapshot = null;

  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
  }

  connectedCallback() {
    if (!this.querySelector?.("[data-health-count]")) {
      this.innerHTML = menuMarkup();
    }
    if (!this.#connected) {
      this.#connected = true;
      this.addEventListener("click", this.handleClick);
      this.addEventListener("change", this.handleChange);
      this.addEventListener("input", this.handleInput);
    }
    this.render();
  }

  disconnectedCallback() {
    if (!this.#connected) {
      return;
    }
    this.#connected = false;
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("change", this.handleChange);
    this.removeEventListener("input", this.handleInput);
  }

  get snapshot() {
    return this.#snapshot;
  }

  set snapshot(value) {
    const preview = value?.diagnosticPreview;
    const previewValid = preview === null || (
      preview
      && typeof preview.reportId === "string"
      && typeof preview.json === "string"
      && preview.json.length <= 16 * 1024
      && Object.keys(preview).sort().join(",") === "json,reportId"
    );
    const descriptionMaxLength = value?.descriptionMaxLength;
    if (
      !value
      || value.version !== 2
      || !Array.isArray(value.visibleIssues)
      || !Number.isInteger(descriptionMaxLength)
      || descriptionMaxLength < 1
      || descriptionMaxLength > 4096
      || !previewValid
    ) {
      throw new TypeError("Page health menu snapshot must use the exact v2 presentation contract.");
    }
    this.#snapshot = value;
    this.render();
  }

  emit(type, detail = {}) {
    this.dispatchEvent(new CustomEvent(type, {
      bubbles: true,
      detail,
    }));
  }

  handleClick(event) {
    const copy = event.target.closest?.("[data-health-copy-id]");
    if (copy) {
      this.emit(PAGE_HEALTH_MENU_EVENTS.copyIssue, {
        issueId: copy.getAttribute("data-health-copy-id"),
      });
      return;
    }
    const dismiss = event.target.closest?.("[data-health-dismiss-id]");
    if (dismiss) {
      this.emit(PAGE_HEALTH_MENU_EVENTS.dismissIssue, {
        issueId: dismiss.getAttribute("data-health-dismiss-id"),
      });
      return;
    }
    if (event.target.closest?.("[data-health-report-confirm]")) {
      this.emit(PAGE_HEALTH_MENU_EVENTS.confirmReport);
      return;
    }
    if (event.target.closest?.("[data-health-report-cancel]")) {
      this.emit(PAGE_HEALTH_MENU_EVENTS.cancelReport);
      return;
    }
    if (event.target.closest?.("[data-health-dismiss-all]")) {
      this.emit(PAGE_HEALTH_MENU_EVENTS.dismissAll);
      return;
    }
    if (event.target.closest?.("[data-health-send-report]")) {
      const issueIds = Array.from(this.querySelectorAll(
        "[data-health-report-select-id]:checked",
      )).map((input) => input.getAttribute("data-health-report-select-id"));
      const description = this.querySelector("[data-health-report-description]")?.value ?? "";
      this.emit(PAGE_HEALTH_MENU_EVENTS.sendReport, { description, issueIds });
    }
  }

  handleChange(event) {
    const menu = event.target.closest?.("[data-health-menu-toggle]");
    if (menu) {
      this.emit(PAGE_HEALTH_MENU_EVENTS.menuVisibilityChange, { enabled: menu.checked === true });
    }
  }

  handleInput(event) {
    if (event.target?.matches?.("[data-health-report-description]")) {
      this.renderReportDescription();
    }
  }

  renderReportDescription() {
    const snapshot = this.#snapshot;
    if (!snapshot) return;
    const description = this.querySelector("[data-health-report-description]");
    const value = String(description?.value ?? "");
    const valid = value.length <= snapshot.descriptionMaxLength && value.trim().length > 0;
    if (description) {
      description.maxLength = snapshot.descriptionMaxLength;
      description.disabled = snapshot.canDescribe !== true;
    }
    text(
      this.querySelector("[data-health-report-description-count]"),
      `${value.length} / ${snapshot.descriptionMaxLength}`,
    );
    setBooleanProperty(
      this.querySelector("[data-health-send-report]"),
      "disabled",
      snapshot.canSend !== true || !valid,
    );
  }

  render() {
    const snapshot = this.#snapshot;
    if (!snapshot || !this.querySelector?.("[data-health-count]")) {
      return;
    }
    this.hidden = snapshot.navMenuVisible !== true;
    if (this.style) {
      this.style.display = snapshot.navMenuVisible === true ? "" : "none";
    }
    this.querySelectorAll("[data-health-count]").forEach((node) => {
      text(node, snapshot.activeWarningCount);
    });
    text(this.querySelector("[data-health-summary]"), snapshot.summary);
    const list = this.querySelector("[data-health-issue-list]");
    if (list) {
      const rootDocument = this.ownerDocument || globalThis.document;
      list.replaceChildren(...snapshot.visibleIssues.map((issue) => issueRow(rootDocument, issue)));
    }
    const preferencesStatus = this.querySelector("[data-health-preferences-status]");
    text(preferencesStatus, snapshot.preferencesStatus);
    if (preferencesStatus) preferencesStatus.hidden = !snapshot.preferencesStatus;
    const sendDetail = this.querySelector("[data-health-send-detail]");
    text(sendDetail, snapshot.sendTitle);
    sendDetail.hidden = snapshot.canSend;
    text(this.querySelector("[data-health-hidden-label]"), snapshot.hiddenLabel);
    setBooleanProperty(
      this.querySelector("[data-health-menu-toggle]"),
      "checked",
      snapshot.menuEnabledByUser === true,
    );
    setBooleanProperty(
      this.querySelector("[data-health-menu-toggle]"),
      "disabled",
      snapshot.preferencesCanChange !== true,
    );
    this.renderReportDescription();
    const preview = snapshot.diagnosticPreview;
    const dialog = this.querySelector("[data-health-report-preview]");
    text(this.querySelector("[data-health-report-preview-json]"), preview?.json || "");
    setBooleanProperty(
      this.querySelector("[data-health-report-confirm]"),
      "disabled",
      !preview,
    );
    if (dialog && preview && !dialog.open) {
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
    } else if (dialog && !preview && dialog.open && typeof dialog.close === "function") {
      dialog.close();
    }
  }
}

export function registerPageHealthMenu(registry = globalThis.customElements) {
  if (registry && !registry.get(PAGE_HEALTH_MENU_TAG)) {
    registry.define(PAGE_HEALTH_MENU_TAG, FishyPageHealthMenu);
  }
}
